'use client'

import Script from 'next/script'
import type { ReactNode } from 'react'

import {
  SITE_ANALYTICS,
  type SiteAnalyticsKey,
} from '../../config/site-analytics'
import StripTrackingParams from './strip-tracking-params'

const SCRIPT_STRATEGY = 'lazyOnload' as const

function nonceProps(nonce?: string): { nonce?: string } {
  return nonce ? { nonce } : {}
}

export function GTMHead({
  containerId = 'GTM-NWBNRSQ6',
  nonce,
}: {
  containerId?: string
  nonce?: string
} = {}): ReactNode {
  return (
    <Script id="gtm-script" strategy={SCRIPT_STRATEGY} {...nonceProps(nonce)}>
      {`
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${containerId}');
      `}
    </Script>
  )
}

export function GTMBody({
  containerId = 'GTM-NWBNRSQ6',
}: {
  containerId?: string
} = {}): ReactNode {
  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${containerId}`}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
      />
    </noscript>
  )
}

function StatcounterHead({
  project,
  security,
  nonce,
}: {
  project: number
  security: string
  nonce?: string
}): ReactNode {
  return (
    <Script
      id={`statcounter-vars-${project}`}
      strategy={SCRIPT_STRATEGY}
      {...nonceProps(nonce)}
    >
      {`
        var sc_project=${project};
        var sc_invisible=1;
        var sc_security="${security}";
      `}
    </Script>
  )
}

function StatcounterExternal({
  project,
  nonce,
}: {
  project: number
  nonce?: string
}): ReactNode {
  return (
    <Script
      id={`statcounter-external-${project}`}
      src="https://www.statcounter.com/counter/counter.js"
      strategy={SCRIPT_STRATEGY}
      {...nonceProps(nonce)}
    />
  )
}

function StatcounterBody({
  project,
  security,
}: {
  project: number
  security: string
}): ReactNode {
  return (
    <noscript>
      <div className="statcounter">
        <a
          title="Web Analytics Made Easy - Statcounter"
          href="https://statcounter.com/"
          target="_blank"
          rel="noreferrer"
        >
          <img
            className="statcounter"
            src={`https://c.statcounter.com/${project}/0/${security}/1/`}
            alt="Web Analytics Made Easy - Statcounter"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </a>
      </div>
    </noscript>
  )
}

function LinkedInInsight({
  partnerId,
  nonce,
}: {
  partnerId: string
  nonce?: string
}): ReactNode {
  const nonceAttr = nonce
    ? `b.setAttribute('nonce', ${JSON.stringify(nonce)});`
    : ''
  return (
    <>
      <Script
        id={`linkedin-partner-${partnerId}`}
        strategy={SCRIPT_STRATEGY}
        {...nonceProps(nonce)}
      >
        {`
          _linkedin_partner_id = "${partnerId}";
          window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
          window._linkedin_data_partner_ids.push(_linkedin_partner_id);
        `}
      </Script>
      <Script
        id={`linkedin-insight-${partnerId}`}
        strategy={SCRIPT_STRATEGY}
        {...nonceProps(nonce)}
      >
        {`
          (function(l) {
            if (!l) {
              window.lintrk = function(a, b) { window.lintrk.q.push([a, b]); };
              window.lintrk.q = [];
            }
            var s = document.getElementsByTagName("script")[0];
            var b = document.createElement("script");
            b.type = "text/javascript";
            b.async = true;
            ${nonceAttr}
            b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
            s.parentNode.insertBefore(b, s);
          })(window.lintrk);
        `}
      </Script>
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          alt=""
          src={`https://px.ads.linkedin.com/collect/?pid=${partnerId}&fmt=gif`}
        />
      </noscript>
    </>
  )
}

/**
 * Loads GTM, StatCounter, and LinkedIn for `site` from {@link SITE_ANALYTICS}.
 * Callers should mount this in production only. `nonce` is optional (legacy CSP).
 */
export function SiteAnalytics({
  site,
  nonce,
}: {
  site: SiteAnalyticsKey
  nonce?: string
}): ReactNode {
  const config = SITE_ANALYTICS[site]
  if (!config) {
    return null
  }

  return (
    <>
      {config.gtm && (
        <GTMHead containerId={config.gtm.containerId} nonce={nonce} />
      )}
      {config.statcounter && (
        <>
          <StatcounterHead
            project={config.statcounter.project}
            security={config.statcounter.security}
            nonce={nonce}
          />
          <StatcounterExternal
            project={config.statcounter.project}
            nonce={nonce}
          />
        </>
      )}
      {config.linkedIn && (
        <LinkedInInsight partnerId={config.linkedIn.partnerId} nonce={nonce} />
      )}
      {config.gtm && <GTMBody containerId={config.gtm.containerId} />}
      {config.statcounter && (
        <StatcounterBody
          project={config.statcounter.project}
          security={config.statcounter.security}
        />
      )}
      <StripTrackingParams />
    </>
  )
}

export {
  SITE_ANALYTICS,
  LINKEDIN_PARTNER_ID,
  STATCOUNTER_REFERENCE,
  type SiteAnalyticsKey,
} from '../../config/site-analytics'
